// /root/FIREBASE MDM/mdm-control/src/utils/wasabiUploader.js
import { S3Client, PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';

// Verify environment variables
const requiredEnvVars = [
  'REACT_APP_WASABI_ACCESS_KEY',
  'REACT_APP_WASABI_SECRET_KEY',
  'REACT_APP_WASABI_BUCKET',
  'REACT_APP_WASABI_REGION',
];
requiredEnvVars.forEach((varName) => {
  // console.log(`${varName}:`, process.env[varName]);
  if (!process.env[varName]) {
    throw new Error(`Missing required environment variable: ${varName}`);
  }
});

// Configure the S3 client for Wasabi
const s3Client = new S3Client({
  region: process.env.REACT_APP_WASABI_REGION,
  endpoint: `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com`,
  credentials: {
    accessKeyId: process.env.REACT_APP_WASABI_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_WASABI_SECRET_KEY,
  },
});

// Helper function to convert File to ArrayBuffer
const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file as ArrayBuffer'));
    reader.readAsArrayBuffer(file);
  });
};

export async function uploadFileToWasabi(file, folder = "MDM") {
  try {
    // console.log('Uploading file:', file.name, 'to folder:', folder);
    // console.log('File type:', file.type, 'File size:', file.size);

    const fileName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
    const key = `${folder}/${fileName}`;
    const bucket = process.env.REACT_APP_WASABI_BUCKET;

    // Convert File to ArrayBuffer
    const fileBuffer = await fileToArrayBuffer(file);

    const command = new PutObjectCommand({
      Bucket: bucket,
      Key: key,
      Body: fileBuffer, // Use ArrayBuffer instead of File
      ContentType: file.type,
      ACL: 'public-read',
    });

    const response = await s3Client.send(command);
    // console.log('Upload response:', response);

    const publicUrl = `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${bucket}/${key}`;

    return {
      publicUrl,
      fileName,
    };
  } catch (error) {
    // console.error('Error uploading to Wasabi:', error);
    throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Upload to an EXACT key, overwriting whatever is there, and return the resulting public URL.
 *
 * uploadFileToWasabi() prefixes every name with Date.now(), which is right for the app store (each
 * version keeps its own object) but wrong for anything whose address has to stay put. A QR
 * enrollment code carries the APK URL baked into it: printed on a sheet, taped inside a box, handed
 * to an installer. That URL has to still resolve after the next agent release, so the provisioning
 * APK lives at one fixed key and new versions overwrite it in place.
 *
 * @param {File} file the APK
 * @param {string} key full object key, e.g. "provisioning/musicdj-mdm.apk"
 * @param {string} [contentType] defaults to the file's own type
 */
export async function uploadFileToWasabiAtKey(file, key, contentType) {
  const bucket = process.env.REACT_APP_WASABI_BUCKET;
  const fileBuffer = await fileToArrayBuffer(file);

  await s3Client.send(new PutObjectCommand({
    Bucket: bucket,
    Key: key,
    Body: fileBuffer,
    ContentType: contentType || file.type || 'application/vnd.android.package-archive',
    ACL: 'public-read',
    // The whole point is a stable address, so make sure nothing serves a stale copy of it.
    CacheControl: 'no-cache, max-age=0',
  }));

  return {
    publicUrl: `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${bucket}/${key}`,
    key,
  };
}

export async function deleteFileFromWasabi(url) {
  try {
    // console.log('Deleting URL:', url);
    const defaultBucket = process.env.REACT_APP_WASABI_BUCKET;

    // Parse bucket and key from URL
    // URL format: https://s3.region.wasabisys.com/bucket/key
    const urlMatch = url.match(/wasabisys\.com\/([^/]+)\/(.+)$/);
    if (!urlMatch) {
      throw new Error('Invalid Wasabi URL: ' + url);
    }

    const bucket = urlMatch[1];
    const key = urlMatch[2];

    const command = new DeleteObjectCommand({
      Bucket: bucket,
      Key: key,
    });

    const response = await s3Client.send(command);
    // console.log(`Successfully deleted ${key} from Wasabi bucket ${bucket}`, response);
  } catch (error) {
    // console.error('Error deleting from Wasabi:', error);
    throw new Error(`Delete failed: ${error.message || 'Unknown error'}`);
  }
}